import { useEffect, useMemo, useState } from 'react';
import { Alert, View, Pressable, InteractionManager, Keyboard } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { Caption, ThemedText } from '@/components/ui/Text';
import { CityAutocomplete } from '@/components/ui/CityAutocomplete';
import { useTheme } from '@/theme';
import { useUserStore, type NotificationPreference } from '@/state/userStore';
import { auth, db } from '@/lib/firebase';
import { COUNTRY_OPTIONS, LANGUAGE_OPTIONS, CURRENCY_OPTIONS } from '@/data/profileOptions';
import { OnboardingProgress } from '@/components/onboarding/OnboardingProgress';
import { getOnboardingStep, getTotalOnboardingSteps } from '@/constants/onboarding';
import { toIso2CountryCode } from '@/utils/countryIso2';
import { LoadingState } from '@/components/ui/LoadingState';
import { withTimeout } from '@/utils/withTimeout';
import { useOnboardingStepAnalytics } from '@/hooks/useOnboardingStepAnalytics';

const PROFILE_SAVE_TIMEOUT_MS = 8000;

export default function ProfileDetailsScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const theme = useTheme();
  const profile = useUserStore((state) => state.profile);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const setProfileDetails = useUserStore((state) => state.setProfileDetails);
  const isNewUser = useUserStore((state) => state.isNewUser);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const authReady = useUserStore((state) => state.authReady);

  const [name, setName] = useState(profile.name ?? '');
  const [homeCountry, setHomeCountry] = useState(profile.homeCountry ?? '');
  const [homeCity, setHomeCity] = useState(profile.homeCity ?? '');
  const [citizenship, setCitizenship] = useState(profile.citizenship ?? '');
  const [language, setLanguage] = useState(profile.language ?? '');
  const [defaultCurrency, setDefaultCurrency] = useState(profile.defaultCurrency ?? 'USD');
  const [nameError, setNameError] = useState<string | undefined>();
  const [homeCountryError, setHomeCountryError] = useState<string | undefined>();
  const defaultNotificationPreference: NotificationPreference = useMemo(
    () => ({
      itinerary: true,
      expenses: true,
      safety: true,
      documents: true,
      collaboration: true,
    }),
    [],
  );
  const [saving, setSaving] = useState(false);

  const rawMode = Array.isArray(params?.mode) ? params.mode[0] : params?.mode;
  const rawReturnTo = Array.isArray(params?.returnTo) ? params.returnTo[0] : params?.returnTo;
  const isEditMode = rawMode === 'edit';
  const { logCompleted } = useOnboardingStepAnalytics('profile-details', !isEditMode);
  const returnTo = useMemo(() => {
    if (rawReturnTo && typeof rawReturnTo === 'string') {
      try {
        return decodeURIComponent(rawReturnTo);
      } catch {
        return rawReturnTo;
      }
    }
    return '/(tabs)/profile';
  }, [rawReturnTo]);

  const canContinue = useMemo(
    () => homeCountry.trim().length > 1,
    [homeCountry],
  );

  useEffect(() => {
    if (!isEditMode && authReady && !isNewUser && hasCompletedOnboarding) {
      router.replace('/(tabs)');
    }
  }, [authReady, hasCompletedOnboarding, isEditMode, isNewUser, router]);

  if (!isEditMode && authReady && !isNewUser && hasCompletedOnboarding) {
    return <LoadingState variant="fullscreen" message="Opening Tripvio…" />;
  }

  const navigateAfterSettledUi = async (target: string) => {
    Keyboard.dismiss();
    await new Promise<void>((resolve) => {
      InteractionManager.runAfterInteractions(() => resolve());
    });
    router.replace(target);
  };

  const handleContinue = async () => {
    const trimmedHomeCountry = homeCountry.trim();
    const nextHomeCountryError =
      trimmedHomeCountry.length > 1 ? undefined : 'Choose your home country to continue.';
    setHomeCountryError(nextHomeCountryError);

    if (isEditMode) {
      const trimmedName = name.trim();
      const nextNameError =
        trimmedName.length === 0 || trimmedName.length > 1
          ? undefined
          : 'Enter at least 2 characters or leave blank to use your sign-in name.';
      setNameError(nextNameError);
      if (nextNameError || nextHomeCountryError) {
        return;
      }
    } else if (nextHomeCountryError) {
      return;
    }

    const authDisplay = auth.currentUser?.displayName?.trim() || '';
    const resolvedName = isEditMode
      ? name.trim() || profile.name?.trim() || authDisplay
      : profile.name?.trim() || authDisplay;

    console.warn('[ProfileDetails] Continue pressed');
    Keyboard.dismiss();
    await new Promise<void>((resolve) => {
      InteractionManager.runAfterInteractions(() => resolve());
    });

    const profileUpdate = {
      ...(resolvedName.trim().length > 0 ? { name: resolvedName.trim() } : {}),
      homeCountry: homeCountry.trim(),
      homeCountryIso2: toIso2CountryCode(homeCountry.trim()),
      homeCity: homeCity.trim() || undefined,
      citizenship: citizenship.trim() || undefined,
      citizenshipIso2: citizenship.trim() ? toIso2CountryCode(citizenship.trim()) : undefined,
      language: language.trim() || undefined,
      defaultCurrency: defaultCurrency || 'USD',
      notificationPreference:
        profile.notificationPreference && Object.values(profile.notificationPreference).some((v) => v !== undefined)
          ? profile.notificationPreference
          : defaultNotificationPreference,
    };

    // If firebaseUser is not available, save locally and continue
    // This handles the case where Firebase connection failed during auth
    if (!firebaseUser?.uid) {
      console.warn('[ProfileDetails] No firebaseUser available, saving locally and continuing');
      setProfileDetails(profileUpdate);
      logCompleted();
      await navigateAfterSettledUi(isEditMode ? returnTo : '/(onboarding)/avatar');
      return;
    }

    setSaving(true);

    try {
      const updatedEmail: string | null = firebaseUser.email
        ? firebaseUser.email.toLowerCase().trim()
        : null;

      await withTimeout(
        setDoc(
          doc(db, 'users', firebaseUser.uid),
          {
            ...profileUpdate,
            homeCity: profileUpdate.homeCity ?? null,
            citizenship: profileUpdate.citizenship ?? null,
            citizenshipIso2: profileUpdate.citizenshipIso2 ?? null,
            homeCountryIso2: profileUpdate.homeCountryIso2 ?? null,
            language: profileUpdate.language ?? null,
            notificationPreference: profileUpdate.notificationPreference ?? null,
            email: updatedEmail ?? null,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ),
        PROFILE_SAVE_TIMEOUT_MS,
        'profile details save',
      );

      setProfileDetails(profileUpdate);

      console.warn('[ProfileDetails] Save complete, navigating');
      if (isEditMode) {
        await navigateAfterSettledUi(returnTo);
      } else {
        logCompleted();
        await navigateAfterSettledUi('/(onboarding)/avatar');
      }
    } catch (error) {
      console.error('Failed to save profile details', error);
      // Fail-open so onboarding can continue even if Firestore (or emulator) is unavailable.
      Alert.alert(
        'Saving offline',
        'Could not save to the server right now, but we will keep your details locally so you can continue.',
      );
      setProfileDetails(profileUpdate);
      console.warn('[ProfileDetails] Save failed or timed out, continuing offline');
      if (!isEditMode) {
        logCompleted();
      }
      await navigateAfterSettledUi(isEditMode ? returnTo : '/(onboarding)/avatar');
    } finally {
      setSaving(false);
    }
  };

  const currentStep = getOnboardingStep('profile-details') ?? 2;
  const totalSteps = getTotalOnboardingSteps();
  const showProgress = !isEditMode;
  const screenTitle = isEditMode ? 'Edit profile' : 'Tell us about you';
  const screenSubtitle = isEditMode
    ? 'Update your basic info and preferences.'
    : "We'll personalize Tripvio with your home base and trip preferences.";
  const showTailoredNote = !isEditMode;

  return (
    <Screen
      title={screenTitle}
      subtitle={screenSubtitle}
      scrollable={true}
      showOfflineIndicator={false}
      showBackButton={true}
      onBack={() => router.back()}
    >
      {showProgress && <OnboardingProgress currentStep={currentStep} totalSteps={totalSteps} />}
      <View style={{ gap: theme.spacing(2) }}>
        {isEditMode ? (
          <InputField
            label="Display name (optional)"
            placeholder="Alex Smith"
            value={name}
            onChangeText={(value) => {
              setName(value);
              if (nameError) setNameError(undefined);
            }}
            autoCapitalize="words"
            returnKeyType="next"
            errorText={nameError}
          />
        ) : null}
        <SelectField
          label="Home country"
          placeholder="Select your home country"
          value={homeCountry}
          onValueChange={(value) => {
            setHomeCountry(value);
            if (homeCountryError) setHomeCountryError(undefined);
          }}
          options={COUNTRY_OPTIONS}
          errorText={homeCountryError}
        />
        <CityAutocomplete
          label="Home city / airport (Optional)"
          placeholder="e.g., Toronto or YYZ"
          value={homeCity}
          onChangeText={setHomeCity}
          autoCapitalize="words"
          allowUseAsEntered
        />
        <Caption color={theme.colors.textSecondary} style={{ marginTop: -theme.spacing(1.25) }}>
          We’ll suggest this first as your departure city when planning trips.
        </Caption>
        <SelectField
          label="Citizenship (Optional)"
          placeholder="Select your citizenship"
          value={citizenship}
          onValueChange={setCitizenship}
          options={COUNTRY_OPTIONS}
          allowClear
        />
        <Caption color={theme.colors.textSecondary} style={{ fontSize: 13, marginTop: -theme.spacing(1.25) }}>
          We will use this to assess visa requirements
        </Caption>
        <SelectField
          label="Preferred language (Optional)"
          placeholder="Select your preferred language"
          value={language}
          onValueChange={setLanguage}
          options={LANGUAGE_OPTIONS}
          allowClear
        />
        
        <SelectField
          label="Default currency"
          placeholder="Select your preferred currency"
          value={defaultCurrency}
          onValueChange={setDefaultCurrency}
          options={CURRENCY_OPTIONS}
        />
        <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, marginTop: -theme.spacing(1.25), lineHeight: 16 }}>
          Select the currency you typically use for budgeting. New trips will default to this currency, and expenses in other currencies will be automatically converted.
        </Caption>

        {showTailoredNote && (
          <View style={{ gap: theme.spacing(1) }}>
            <Caption color={theme.colors.textSecondary}>
              We’ll use this to tailor currency, weather, and visa reminders. Notification preferences live in Settings.
            </Caption>
            <Pressable
              onPress={() => router.push('/(tabs)/profile')}
              style={({ pressed }) => ({
                alignSelf: 'flex-start',
                paddingVertical: theme.spacing(0.25),
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <ThemedText variant="bodyStrong" color={theme.colors.primary}>
                Manage notifications in Settings
              </ThemedText>
            </Pressable>
          </View>
        )}
      </View>

      <Button
        label={isEditMode ? 'Save changes' : 'Continue'}
        onPress={handleContinue}
        disabled={!canContinue || saving}
        loading={saving}
        style={{ opacity: canContinue && !saving ? 1 : 0.6 }}
      />
    </Screen>
  );
}


